import { loadStripe } from '@stripe/stripe-js';
import api from './api';

class StripeService {
  constructor() {
    this.stripePromise = null;
    this.publishableKey = null;
    this.apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8085';
  }

  /**
   * Helper pour faire des requêtes API avec fetch au lieu d'axios
   */
  async apiRequest(endpoint, options = {}) {
    const url = `${this.apiUrl}/api${endpoint}`;
    const defaultOptions = {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    };

    // Ajouter le token JWT si disponible
    const token = localStorage.getItem('token');
    if (token) {
      defaultOptions.headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...(options.headers || {})
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Initialiser Stripe avec la clé publique
   */
  async initialize() {
    try {
      if (!this.publishableKey) {

        const data = await this.apiRequest('/payment/config');

        this.publishableKey = data.config.publishable_key;
      }
      
      if (!this.stripePromise && this.publishableKey) {
        this.stripePromise = loadStripe(this.publishableKey);
      }
      
      return this.stripePromise;
    } catch (error) {
      console.error('Erreur lors de l\'initialisation de Stripe:', error);
      console.error('Détails de l\'erreur:', error.response?.data || error.message);
      throw error;
    }
  }

  /**
   * Obtenir l'instance Stripe
   */
  async getStripe() {
    if (!this.stripePromise) {
      await this.initialize();
    }
    return this.stripePromise;
  }

  /**
   * Créer un PaymentIntent pour un abonnement
   */
  async createPaymentIntent(subscriptionId, activeSites, isAnnual = false) {
    try {
      const data = await this.apiRequest('/payment/create-intent', {
        method: 'POST',
        body: JSON.stringify({
          subscription_id: subscriptionId,
          active_sites: activeSites,
          is_annual: isAnnual
        })
      });

      return data;
    } catch (error) {
      console.error('Erreur lors de la création du PaymentIntent:', error);
      throw error;
    }
  }  /**
   * Calculer le prix pour l'inscription (sans utilisateur connecté)
   */
  async calculateRegistrationPrice(subscriptionId, activeSites, isAnnual = false, email = '') {
    try {
      const response = await api.post('/payment/calculate-pricing', {
        subscription_id: subscriptionId,
        active_sites: activeSites,
        is_annual: isAnnual,
        email
      });
      
      return response.data;
    } catch (error) {
      console.error('Erreur lors du calcul du prix:', error);
      throw error;
    }
  }

  /**
   * Confirmer un paiement
   */
  async confirmPayment(paymentIntentId) {
    try {
      const response = await api.post(`/payment/confirm-payment`, {
        payment_intent_id: paymentIntentId
      });
      
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la confirmation du paiement:', error);
      throw error;
    }
  }

  /**
   * Annuler un PaymentIntent
   */
  async cancelPayment(paymentIntentId) {
    try {
      const response = await api.post(`/payment/cancel/${paymentIntentId}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'annulation du paiement:', error);
      throw error;
    }
  }

  /**
   * Traiter un paiement avec Stripe Elements
   */
  async processPayment(stripe, elements, clientSecret, userInfo = {}) {
    try {
      // Étape 1 : Soumettre les éléments d'abord
      const { error: submitError } = await elements.submit();
      
      if (submitError) {
        console.error('Erreur lors de la soumission des éléments:', submitError);
        return {
          success: false,
          error: submitError.message,
          code: submitError.code
        };
      }

      // Étape 2 : Confirmer le paiement après soumission réussie
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: window.location.origin + '/payment/success',
          payment_method_data: {
            billing_details: {
              name: userInfo.name || '',
              email: userInfo.email || '',
              address: {
                line1: userInfo.address || '',
                postal_code: userInfo.zipCode || '',
                city: userInfo.city || '',
                country: userInfo.country || 'FR',
              }
            }
          }
        },
        redirect: 'if_required'
      });

      if (error) {
        console.error('Erreur de paiement:', error);
        return {
          success: false,
          error: error.message,
          code: error.code
        };
      }

      if (paymentIntent.status === 'succeeded') {
        return {
          success: true,
          paymentIntent,
          message: 'Paiement réussi !'
        };
      }

      return {
        success: false,
        error: 'Le paiement n\'a pas pu être traité',
        status: paymentIntent.status
      };

    } catch (error) {
      console.error('Erreur lors du traitement du paiement:', error);
      return {
        success: false,
        error: error.message || 'Une erreur inattendue s\'est produite'
      };
    }
  }

  /**
   * Obtenir l'historique des paiements
   */
  async getPaymentHistory() {
    try {
      const response = await api.get('/payment/history');
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'historique:', error);
      throw error;
    }
  }

  /**
   * Récupérer les détails d'un paiement
   */
  async getPaymentDetails(paymentIntentId) {
    try {
      const data = await this.apiRequest(`/payment/details/${paymentIntentId}`);
      return data.payment_intent;
    } catch (error) {
      console.error('Erreur lors de la récupération des détails du paiement:', error);
      throw error;
    }
  }

  /**
   * Confirmer le succès du paiement côté serveur
   */
  async confirmPaymentSuccess(paymentIntentId) {
    try {
      const data = await this.apiRequest(`/payment/confirm-success/${paymentIntentId}`, {
        method: 'POST'
      });
      return data;
    } catch (error) {
      console.error('Erreur lors de la confirmation du paiement:', error);
      throw error;
    }
  }

  /**
   * Utilitaire pour formater le montant en euros
   */
  formatAmount(amount) {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  }
  
  /**
   * Obtenir la configuration publique
   */
  async getPublicConfig() {
    try {
      const response = await api.get('/payment/config');
      return response.data.config;
    } catch (error) {
      console.error('Erreur lors de la récupération de la configuration:', error);
      throw error;
    }
  }
}

export default new StripeService();